import appConstants from "../../../../appConstants";
import { getDataWithQueryString } from "../../../../api/componentAPIs";
import { formatMonthDateYear } from '@dataGrid/gridUtilities';
import { APPOINTMENT_TYPES, CLOSED_WINDOW_LABEL, EQUIPMENT_FLOW, CUSTOMER_DETAILS  } from "./constants";
import { FORM_VALIDATION_MSG } from "../../Dispatch/RouteExecution/Pickups/PickupManagement/constants";

const getNoFMEOptions = (isNoFME = true) => [
  {
    name: 'isNoFME',
    labelText: 'Not an FME Customer',
    isChecked: isNoFME,
    disabled: false
  }
];

const getNoCargoVanOptions = (isNoCargoVan = false) => [
  {
    name: 'isNoCargoVan',
    labelText: 'No Cargo Van',
    isChecked: isNoCargoVan,
    disabled: false
  }
];


const getDeliveryAccessorialList = ({
  isLiftGateRequired = false,
  isNoLg = false,
  isIgnoreLg = false,
  isNonLGOptionsRequired = true,
}) => [
  {
    name: 'isLiftGateRequired',
    labelText: 'Requires LG',
    isChecked: isLiftGateRequired,
    disabled: false
  },
  ...(isNonLGOptionsRequired ? [
    {
      name: "isIgnoreLg",
      labelText: "Ignore LG",
      isChecked: isIgnoreLg,
      disabled: false,
    },
    {
      name: "isNoLg",
      labelText: "Don't load on LG Trailer",
      isChecked: isNoLg,
      disabled: false,
    },
  ]: []),
];

const getPickupAccessorialList = ({
  isLiftGateRequired = false,
  isTrailerSwap = false,
  enableTrailerSwap = false
}) => [
  {
    name: 'isLiftGateRequired',
    labelText: 'Requires LG',
    isChecked: isLiftGateRequired,
    disabled: false
  },
  ...(enableTrailerSwap
    ? [
        {
          name: 'isTrailerSwap',
          labelText: 'Trailer Swap',
          isChecked: isTrailerSwap,
          disabled: false
        }
      ]
    : [])
];

const getDefaultCustomerErrorInfo = () => {
  return {
    receivingHours: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: []
    },
    defaultHours: [],
    noFMECustomer: ''
  };
};

const getNoFMECustomerErrorInfo = ({ noFMECustomerOptions, equipmentDetails }) => {
  /* Condition for SEFL to not allow update:
    1. maxEquipmentSize=appConstants.FME_ROUTE_TRAILER_LENGTH,
    2. No FME Customer checkbox selected
  */
  let equipmentSize = null;
  if (equipmentDetails[equipmentDetails.equipmentType] === EQUIPMENT_FLOW.FIXED) {
    equipmentSize = equipmentDetails[equipmentDetails.equipmentType].fixedEquipmentSize;
  } else if (equipmentDetails[equipmentDetails.equipmentType] === EQUIPMENT_FLOW.FLEXIBLE) {
    equipmentSize =
      equipmentDetails[equipmentDetails.equipmentType].maxEquipmentSize ||
      equipmentDetails[equipmentDetails.equipmentType].minEquipmentSize;
  }
  return noFMECustomerOptions && equipmentSize === appConstants.FME_ROUTE_TRAILER_LENGTH
    ? `Equip Size can't be ${appConstants.FME_ROUTE_TRAILER_LENGTH} for non-FME customers`
    : null;
};

const isDeliveryTab = (activeTab) => {
  return activeTab === appConstants.CUSTOMER_TAB_TYPE.DELIVERY;
};

const isPickupTab = (activeTab) => {
  return activeTab === appConstants.CUSTOMER_TAB_TYPE.PICKUP;
};
const getAllHolidays = async ({ enqueueSnackbar, terminalId }) => {
  try {
    const response = await getDataWithQueryString(
      'GetTerminalHolidays',
      `terminalId=${terminalId}`
    );
    if (response && response.status >= 200 && response.status < 400) {
      return response.data;
    }
    return handleHolidayFetchError(enqueueSnackbar);
  } catch (_error) {
    return handleHolidayFetchError(enqueueSnackbar);
  }
};

const getFormattedHolidayOptions = (holidays) => {
  return holidays.map((holiday) => {
    const formattedHolidayDate = formatMonthDateYear({ value: holiday.holidayDate });
    return {
      label: holiday.holidayName,
      value: holiday.holidayId,
      holidayDate: formattedHolidayDate,
      labelSuffix: formattedHolidayDate,
      labelSuffixNewLine: true
    };
  });
};

const getHolidayOptions = async ({enqueueSnackbar, terminalId}) => {
  const holidays = await getAllHolidays({enqueueSnackbar, terminalId});
  return getFormattedHolidayOptions(holidays);
}

const handleHolidayFetchError = (enqueueSnackbar) => {
  enqueueSnackbar('Failed to fetch holidays', {
    variant: 'error'
  });
  return [];
};

const processDefaultHours = (defaultWindow) => {
  const defaultHours = defaultWindow.filter(Boolean);
  const getTrimmedOpenAndCloseHours = (hours) => hours.trim().split('-').map((time) => time.trim());

  if (!defaultHours[0]) {
    return { openHours: '', closeHours: '', openHours2: null, closeHours2: null };
  }

  const [openHours, closeHours] = getTrimmedOpenAndCloseHours(defaultHours[0]);

  if (!defaultHours[1]) {
    return { openHours, closeHours, openHours2: null, closeHours2: null };
  }

  const [openHours2, closeHours2] = getTrimmedOpenAndCloseHours(defaultHours[1]);

  return { openHours, closeHours, openHours2, closeHours2 };
};

const getTrimmedHours = (hours) => hours.trim().split(' ').join(' - ');

const getFormattedDefaultHours = ({ openHours, closeHours, openHours2, closeHours2 }) => {
  if (!openHours || !closeHours) {
    // We need to add null as default value for open/close hours to handle the input element when API returns null values
    return [null];
  }
  const getTrimmedValues = (openHours, closeHours) =>
    getTrimmedHours(`${openHours ?? ''} ${closeHours ?? ''}`);
    const defaultHours = [getTrimmedValues(openHours, closeHours)];

  if (openHours2 || closeHours2) {
    defaultHours.push(getTrimmedValues(openHours2, closeHours2));
  }

  return defaultHours;
};

const getEquipmentPayloadData = ({ enableFixedEquipmentSize, equipmentDetails }) => {
  if (!enableFixedEquipmentSize) {
    return {
      minEquipmentSize: equipmentDetails[EQUIPMENT_FLOW.FLEXIBLE]?.minEquipmentSize || null,
      maxEquipmentSize: equipmentDetails[EQUIPMENT_FLOW.FLEXIBLE]?.maxEquipmentSize || null,
      fixedEquipmentSize: undefined
    };
  }
  if (equipmentDetails.equipmentType === EQUIPMENT_FLOW.FIXED) {
    return {
      fixedEquipmentSize: equipmentDetails[EQUIPMENT_FLOW.FIXED]?.fixedEquipmentSize || null,
      maxEquipmentSize: null,
      minEquipmentSize: null
    };
  }
  return {
    fixedEquipmentSize: null,
    maxEquipmentSize: equipmentDetails[EQUIPMENT_FLOW.FLEXIBLE]?.maxEquipmentSize || null,
    minEquipmentSize:
      equipmentDetails[EQUIPMENT_FLOW.FLEXIBLE]?.minEquipmentSize || null
  };
};

const getEquipmentTypeOption = (enableFixedEquipment) => {
  return [
    ...(enableFixedEquipment
      ? [
          {
            label: EQUIPMENT_FLOW.FIXED,
            value: EQUIPMENT_FLOW.FIXED
          }
        ]
      : []),
    { label: EQUIPMENT_FLOW.FLEXIBLE, value: EQUIPMENT_FLOW.FLEXIBLE }
  ];
};


const getEquipmentDetails = ({
  enableFixedEquipmentSize,
  fixedEquipmentSize,
  maxEquipmentSize,
  minEquipmentSize
}) => {
  let equipmentType = null;
  if (enableFixedEquipmentSize && fixedEquipmentSize) {
    equipmentType = EQUIPMENT_FLOW.FIXED;
  } else if (maxEquipmentSize || minEquipmentSize) {
    equipmentType = EQUIPMENT_FLOW.FLEXIBLE;
  }
  return {
    equipmentType,
    [EQUIPMENT_FLOW.FIXED]: enableFixedEquipmentSize ? { fixedEquipmentSize } : undefined,
    [EQUIPMENT_FLOW.FLEXIBLE]: {
      minEquipmentSize,
      maxEquipmentSize
    }
  };
}

const renderWindow = (params) => {
  let style = {}
  if(params.valueFormatted === CLOSED_WINDOW_LABEL){
     style = {opacity:0.7, fontStyle:"italic"}
  }
  return <div style={style}>{params.valueFormatted}</div>;
}

const getDefaultHours = ({ openHours, closeHours, openHours2, closeHours2 }) => {
  if (!openHours && !closeHours) {
    return null;
  }
  return getFormattedDefaultHours({ openHours, closeHours, openHours2, closeHours2 });
};

const formatDayWindow = (params) => {
  if (params.value?.length === 1 && params.value[0] === "00:00 - 00:00") {
    return CLOSED_WINDOW_LABEL;
  }
  return params.value?.join(",");
};

const getAPTRadioValue = (isApt) => {
  return isApt ? APPOINTMENT_TYPES.REQUIRES_APT : APPOINTMENT_TYPES.IGNORE_APT;
};

const getInitialDeliveryDetails = ({
  deliveryDetails,
  prevReceivingHours,
  enableFixedEquipmentSize,
  featureFlags
}) => {
  return {
    receivingHours: deliveryDetails?.receivingHours
      ? {
          ...prevReceivingHours,
          ...deliveryDetails.receivingHours
        }
      : null,
    defaultHours: getFormattedDefaultHours({
      openHours: deliveryDetails?.openHours,
      closeHours: deliveryDetails?.closeHours,
      openHours2: deliveryDetails?.openHours2,
      closeHours2: deliveryDetails?.closeHours2
    }),
    equipmentDetails: getEquipmentDetails({
      enableFixedEquipmentSize,
      fixedEquipmentSize: deliveryDetails?.fixedEquipmentSize,
      maxEquipmentSize: deliveryDetails?.maxEquipmentSize,
      minEquipmentSize: deliveryDetails?.minEquipmentSize
    }),
    accessorialList: getDeliveryAccessorialList({
      isLiftGateRequired: deliveryDetails?.isLiftGateRequired,
      isNoLg: deliveryDetails?.isNoLg,
      isIgnoreLg: deliveryDetails?.isIgnoreLg,
      isNonLGOptionsRequired: featureFlags?.enableNonLGCustomer,
    }),
    noFMECustomerOptions: getNoFMEOptions(deliveryDetails?.isNoFME),
    noCargoVanCustomerOptions: getNoCargoVanOptions(deliveryDetails?.isNoCargoVan),
    minimumHUValue: deliveryDetails?.minHuForApt ?? null,
    appointmentValue:
      !deliveryDetails?.isApt && !deliveryDetails?.isIgnoreApt
        ? null
        : getAPTRadioValue(deliveryDetails?.isApt)
  };
};

const getInitialPickupDetails = ({ pickupDetails, enableFixedEquipmentSize, enableTrailerSwap, prevReceivingHours }) => {
  return {
    shippingHours: pickupDetails?.shippingHours
      ? {
          ...prevReceivingHours,
          ...pickupDetails.shippingHours
        }
      : null,
    defaultHours: getFormattedDefaultHours({
      openHours: pickupDetails?.openHours,
      closeHours: pickupDetails?.closeHours
    }),
    equipmentDetails: getEquipmentDetails({
      enableFixedEquipmentSize,
      fixedEquipmentSize: pickupDetails?.fixedEquipmentSize,
      maxEquipmentSize: pickupDetails?.maxEquipmentSize,
      minEquipmentSize: pickupDetails?.minEquipmentSize
    }),
    accessorialList: getPickupAccessorialList({
      isLiftGateRequired: pickupDetails?.isLiftGateRequired,
      isTrailerSwap: pickupDetails?.isTrailerSwap,
      enableTrailerSwap
    })
  };
};

const getPhoneNumberErrorInfo = (details) => {
  const hasError = details.phoneNumber && details.phoneNumber.length < 10;
  return hasError ? FORM_VALIDATION_MSG.INVALID_PHONE_NUMBER : "";
}

const getCustomerDetailsKey = (activeTab) => {
  return isDeliveryTab(activeTab) ? CUSTOMER_DETAILS.DELIVERY : CUSTOMER_DETAILS.PICKUP;
};

export {
  getNoFMEOptions,
  getDeliveryAccessorialList,
  getPickupAccessorialList,
  getDefaultCustomerErrorInfo,
  getNoFMECustomerErrorInfo,
  isDeliveryTab,
  getHolidayOptions,
  getFormattedHolidayOptions,
  processDefaultHours,
  getFormattedDefaultHours,
  getNoCargoVanOptions,
  getEquipmentPayloadData,
  renderWindow,
  getDefaultHours,
  formatDayWindow,
  isPickupTab,
  getInitialDeliveryDetails,
  getInitialPickupDetails,
  getEquipmentTypeOption,
  getEquipmentDetails,
  getPhoneNumberErrorInfo,
  getCustomerDetailsKey
};
